import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import Modal from '../../components/Modal';
import ExportToCSVButton from '../../components/ExportToCSVButton';
import DetailInvoice from './DetailInvoice';
import { useSelector } from 'react-redux';
import client from '../../api';
import { picState } from '../../features/pic/picSlice';
import allowedCustomers from '../../data/allowedCustomers';
import DateRangeSiberat from '../../components/DateRange';
import { Icon } from '@iconify/react';
import { Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TableSiberat from '../../components/table/TableSiberat';
import usePageTitle from '../../hooks/usePageTitle';

const STATUS_FILTERS = [
  { id: 1, text: 'Semua Status', val: '' },
  { id: 2, text: 'Belum Bayar', val: 'belum_bayar' },
  { id: 3, text: 'Partial', val: 'cicil' },
  { id: 4, text: 'Overdue', val: 'overdue' },
  { id: 5, text: 'Lunas', val: 'lunas' }
];

const formatMoneyText = value => {
  const num = parseInt(value ?? 0);
  return `Rp ${Number.isFinite(num) ? num.toLocaleString('id-ID') : '0'}`;
};

const MoneyBreakdown = ({ subtotal, tax, insurance, total }) => (
  <div className='invoice-money'>
    <div className='invoice-money__row'>
      <span className='invoice-money__label'>Sub Total</span>
      <span className='invoice-money__value'>{formatMoneyText(subtotal)}</span>
    </div>
    <div className='invoice-money__row'>
      <span className='invoice-money__label'>PPN</span>
      <span className='invoice-money__value'>{formatMoneyText(tax)}</span>
    </div>
    <div className='invoice-money__row'>
      <span className='invoice-money__label'>Asuransi</span>
      <span className='invoice-money__value'>{formatMoneyText(insurance)}</span>
    </div>
    <div className='invoice-money__row is-total'>
      <span className='invoice-money__label'>Total Invoice</span>
      <span className='invoice-money__value'>{formatMoneyText(total)}</span>
    </div>
  </div>
);

const Invoice = () => {
  usePageTitle('Invoice');
  const { user } = useSelector(state => state.auth);
  const pic = useSelector(picState);
  const [sortBy] = useState('inv_tgl');
  const [sortOrder] = useState('desc');
  const [invoiceData, setInvoiceData] = useState({
    rowData: [],
    isLoading: false,
    totalPages: 0,
    totalDataAll: 0,
    totalDataPages: 0,
    subtotal: null,
    total_tax: null,
    total_ins: null,
    total_invoice: null
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sizePage, setSizePage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [isDetailModalOpen, toggleDetailModal] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [statusInvoice, setInvoiceOrder] = useState('');
  const [search, setSearch] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [dateFilter, setdateFilter] = useState({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD')
  });

  const checkStatusInvoice = (paid_value, total_invtax, ar_overdue) => {
    if (paid_value === total_invtax && ar_overdue === 'Done') {
      return <span className='invoice-status-pill is-lunas'>Lunas</span>;
    }

    if (!paid_value && ar_overdue <= 0) {
      return <span className='invoice-status-pill is-unpaid'>Belum Bayar</span>;
    }

    if (!paid_value && ar_overdue > 0) {
      return <span className='invoice-status-pill is-overdue'>Overdue</span>;
    }

    if (parseFloat(paid_value) < parseFloat(total_invtax)) {
      return <span className='invoice-status-pill is-partial'>Partial</span>;
    }

    return '-';
  };

  const arOverdueDisplay = ar_overdue => {
    if (ar_overdue === 'Done') {
      return (
        <div className='invoice-ar'>
          <span className='invoice-ar__label'>AR Overdue</span>
          <span className='invoice-ar-badge is-done'>Done</span>
        </div>
      );
    }

    const days = parseInt(ar_overdue);
    const tone = days > 0 ? 'is-late' : days < 0 ? 'is-early' : 'is-due';

    return (
      <div className='invoice-ar'>
        <span className='invoice-ar__label'>AR Overdue</span>
        <span className={`invoice-ar-badge ${tone}`}>{ar_overdue} Hari</span>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      { label: 'No', field: 'no', width: '4%', verticalAlign: 'middle' },
      {
        label: 'Invoice',
        field: 'invoice',
        width: '20%',
        verticalAlign: 'middle'
      },
      {
        label: 'Nominal',
        field: 'total',
        width: '28%',
        align: 'right',
        verticalAlign: 'middle'
      },
      {
        label: 'Jatuh Tempo',
        field: 'due',
        width: '18%',
        verticalAlign: 'middle'
      },
      {
        label: 'Status',
        field: 'status',
        width: '16%',
        align: 'center',
        verticalAlign: 'middle'
      },
      {
        label: '',
        field: 'detail',
        width: '6%',
        align: 'center',
        verticalAlign: 'middle'
      }
    ],
    []
  );

  const rows = useMemo(() => {
    return (invoiceData?.rowData || []).map((item, index) => ({
      id: parseInt(item?.id),
      no: (
        <span className='order-cell__date'>
          {(currentPage - 1) * sizePage + index + 1}.
        </span>
      ),
      invoice: (
        <div className='order-cell'>
          <span className='order-cell__no'>{item?.inv_no || '-'}</span>
          <span className='order-cell__date'>
            {moment(item?.inv_tgl).format('DD MMM YYYY')}
          </span>
        </div>
      ),
      total: (
        <MoneyBreakdown
          subtotal={item?.total_inv}
          tax={item?.total_tax}
          insurance={item?.tot_ins}
          total={item?.total_invtax}
        />
      ),
      due: (
        <div className='order-cell'>
          <span className='invoice-due-date'>
            {moment(item?.inv_due).format('DD MMM YYYY')}
          </span>
          {arOverdueDisplay(item?.ar_overdue)}
        </div>
      ),
      status: (
        <div className='order-cell-center'>
          {checkStatusInvoice(
            item?.paid_value,
            item?.total_invtax,
            item?.ar_overdue
          )}
        </div>
      ),
      detail: (
        <div className='order-cell-center'>
          <Tooltip title='Lihat rincian invoice' placement='top' arrow>
            <button
              type='button'
              className='list-page__icon-btn'
              onClick={() => {
                setOpenModal(true);
                toggleDetailModal(true);
                setInvoiceDetail(item);
              }}>
              <Icon icon='solar:menu-dots-bold' width={18} />
            </button>
          </Tooltip>
        </div>
      )
    }));
  }, [invoiceData?.rowData, currentPage, sizePage]);

  const summaryItems = [
    { label: 'Sub Total', value: invoiceData?.subtotal },
    { label: 'PPN', value: invoiceData?.total_tax },
    { label: 'Asuransi', value: invoiceData?.total_ins },
    { label: 'Total Invoice', value: invoiceData?.total_invoice, emphasize: true }
  ];

  const handleStatus = newValue => {
    setInvoiceOrder(newValue);
    setCurrentPage(1);
  };

  const handleRefresh = () => setRefresh(prev => !prev);

  const getData = async (pageNo = 1, size = 10) => {
    const response = await client.get(
      `api/customers/invoice?page=${pageNo}&sort_by=${sortBy}&sort_order=${sortOrder}&from=${dateFilter.startDate}&until=${dateFilter.endDate}&size=${size}&invoice_status=${statusInvoice}&search=${debouncedSearch}&branch=${pic}`,
      {
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${user.data.token}`
        }
      }
    );
    return response.data;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset page saat filter berubah — jangan sync page dari API (hindari race/loop)
  useEffect(() => {
    setCurrentPage(prev => (prev === 1 ? prev : 1));
  }, [
    debouncedSearch,
    statusInvoice,
    dateFilter.startDate,
    dateFilter.endDate,
    pic
  ]);

  useEffect(() => {
    let cancelled = false;

    setInvoiceData(prevState => ({
      ...prevState,
      rowData: [],
      isLoading: true
    }));

    getData(currentPage, sizePage)
      .then(info => {
        if (cancelled) return;
        const {
          count,
          rowCount,
          rows,
          totalPages: total_pages,
          subtotal,
          total_tax,
          total_ins,
          total_invoice
        } = info;

        setInvoiceData({
          isLoading: false,
          rowData: rows,
          totalPages: total_pages,
          subtotal,
          total_tax,
          total_ins,
          total_invoice,
          totalDataPages: rowCount,
          totalDataAll: count
        });
      })
      .catch(() => {
        if (cancelled) return;
        setInvoiceData(prevState => ({
          ...prevState,
          rowData: [],
          isLoading: false
        }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    sortOrder,
    sortBy,
    dateFilter.startDate,
    dateFilter.endDate,
    statusInvoice,
    sizePage,
    debouncedSearch,
    refresh,
    pic
  ]);

  return (
    <main className='list-page p-4 sm:p-5 space-y-6'>
      <div className='list-page__header'>
        <div className='list-page__title-wrap'>
          <div className='list-page__title-copy'>
            <h1 className='list-page__title'>Invoice</h1>
            <p className='list-page__subtitle'>
              Ringkasan tagihan dan status pembayaran
            </p>
          </div>
        </div>
        <div className='list-page__actions'>
          <DateRangeSiberat
            value={`${
              dateFilter.startDate
                ? moment(dateFilter.startDate).format('D MMM')
                : moment().startOf('month').format('D MMM')
            } - ${
              dateFilter.endDate
                ? moment(dateFilter.endDate).format('D MMM YYYY')
                : moment().format('D MMM YYYY')
            }`}
            start='startDate'
            end='endDate'
            params={dateFilter}
            setParams={setdateFilter}
          />
          {Object.hasOwn(allowedCustomers, user.data.name) && (
            <ExportToCSVButton
              fileName={`invoice-${statusInvoice}`}
              statusInvoice={statusInvoice}
            />
          )}
          <button
            type='button'
            className={`list-page__refresh${
              invoiceData.isLoading ? ' is-spinning' : ''
            }`}
            disabled={invoiceData.isLoading}
            onClick={handleRefresh}
            aria-label='Refresh invoice'>
            <Icon icon='solar:refresh-bold' width={18} />
          </button>
        </div>
      </div>

      <div className='list-page__card'>
        <div className='list-page__toolbar'>
          <div className='list-page__toolbar-left'>
            <div className='list-page__search'>
              <SearchIcon className='list-page__search-icon' fontSize='small' />
              <input
                type='text'
                className='list-page__search-input'
                placeholder='Cari data...'
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type='button'
                  onClick={() => setSearch('')}
                  aria-label='Clear search'
                  className='list-page__search-clear'>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          </div>

          <div className='list-page__chips'>
            {STATUS_FILTERS.map(val => (
              <button
                key={val.id}
                type='button'
                onClick={() => handleStatus(val.val)}
                className={`list-page__chip${
                  statusInvoice === val.val ? ' is-active' : ''
                }`}>
                {val.text}
              </button>
            ))}
          </div>
        </div>

        <div className='invoice-summary'>
          {summaryItems.map(item => (
            <div
              key={item.label}
              className={`invoice-summary__item${
                item.emphasize ? ' is-emphasize' : ''
              }`}>
              <span className='invoice-summary__label'>{item.label}</span>
              <span className='invoice-summary__value'>
                {item.value || '-'}
              </span>
            </div>
          ))}
        </div>

        <TableSiberat
          columns={columns}
          rows={rows}
          loading={invoiceData.isLoading}
          pagination
          page={currentPage}
          pageCount={invoiceData.totalPages || 1}
          pageSize={sizePage}
          total={invoiceData.totalDataAll || 0}
          onPageChange={value => setCurrentPage(value)}
          onPageSizeChange={value => {
            setSizePage(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {isDetailModalOpen ? (
        <Modal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            toggleDetailModal(false);
            setInvoiceDetail(null);
          }}
          customTitle={true}
          title={
            <div className='invoice-detail__title'>
              <h2 className='siberat-modal__heading'>Rincian Invoice</h2>
              <span className='invoice-detail__title-no'>
                {invoiceDetail?.inv_no || '-'}
              </span>
              {checkStatusInvoice(
                invoiceDetail?.paid_value,
                invoiceDetail?.total_invtax,
                invoiceDetail?.ar_overdue
              )}
            </div>
          }
          width='w-10/12'>
          <DetailInvoice
            detailInvoice={invoiceDetail}
            onClose={() => {
              setOpenModal(false);
              toggleDetailModal(false);
              setInvoiceDetail(null);
            }}
          />
        </Modal>
      ) : null}
    </main>
  );
};

export default Invoice;
